"use client";
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  navigationMenuTriggerStyle,
} from "@/client/components/common/NavigationMenu";
import { cn } from "@/client/lib/classnames";
import { NavigationMenuTrigger } from "@radix-ui/react-navigation-menu";
import Link from "next/link";
import React from "react";
import { useAuth } from "../AuthProvider";
import { NavItem } from "./constants";
import { UserCircleConnectedIcon, UserIcon } from "./icons";
import {
  getListingsPath,
  getOffersPath,
  getSellPath,
  getWalletPath,
} from "@/client/lib/links";

const getAccountSubItems = ({
  walletAddress,
  onDisconnect,
}: {
  walletAddress?: string;
  onDisconnect: () => void;
}): Array<NavItem> | undefined => {
  if (!walletAddress) {
    return undefined;
  }

  return [
    {
      label: "Wallet",
      value: "wallet",
      href: getWalletPath(walletAddress),
    },
    {
      label: "Sell",
      value: "sell",
      href: getSellPath(),
    },
    {
      label: "Manage listings",
      value: "manage-listings",
      href: getListingsPath(),
    },
    {
      label: "Manage offers",
      value: "manage-offers",
      href: getOffersPath(walletAddress),
    },
    {
      label: "Sign Out",
      value: "sign-out",
      onClick: onDisconnect,
    },
  ];
};

export default function AccountNavigationMenuItem(): React.ReactElement {
  const {
    login,
    isAuthenticated,
    isReady: isAuthReady,
    address,
    logout,
  } = useAuth();
  const accountSubItems = getAccountSubItems({
    walletAddress: address,
    onDisconnect: () => {
      logout?.();
    },
  });

  const getButton = () => {
    if (!isAuthReady) {
      return (
        <div
          className={cn(
            navigationMenuTriggerStyle(),
            "user-select-none pointer-events-none flex items-center justify-center"
          )}
        >
          <div className="border rounded-full border-outline-variant w-sm h-sm" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <button
          onClick={login}
          className={cn(
            navigationMenuTriggerStyle(),
            "flex items-center justify-center"
          )}
        >
          <UserIcon className={`fill-on-surface w-sm h-sm`} />
        </button>
      );
    }

    return (
      <>
        <NavigationMenuTrigger
          className={cn(
            navigationMenuTriggerStyle(),
            "flex items-center justify-center"
          )}
        >
          <UserIcon className={`w-sm h-sm fill-on-surface }`} />
        </NavigationMenuTrigger>
        <NavigationMenuContent contentFor="account" align="end">
          {accountSubItems?.map((item) => (
            <NavigationMenuLink
              key={item.value}
              href={item.href ?? ""}
              className="block min-w-full"
              asChild
            >
              {item.onClick ? (
                <button className="text-left" onClick={item.onClick}>
                  {item.label}
                </button>
              ) : (
                <Link href={item.href ?? ""}>{item.label}</Link>
              )}
            </NavigationMenuLink>
          ))}
        </NavigationMenuContent>
      </>
    );
  };

  return (
    <NavigationMenuItem value="account">
      <div className="relative">{getButton()}</div>
    </NavigationMenuItem>
  );
}
